import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CustomerSalesOrdersService, Order, SalesOrdersService } from '@vendasta/sales-orders';
import { catchError, skipWhile, take, tap } from 'rxjs/operators';

interface OrderState {
  order: Order;
  loading: boolean;
}

export interface SalesOrders {
  get(orderId, businessId): Observable<Order>;
}

@Injectable()
export class SalesOrderStoreService {
  private ordersStore: Map<string, BehaviorSubject<OrderState>> = new Map<string, BehaviorSubject<OrderState>>();

  constructor(
    @Inject(SalesOrdersService) private salesOrdersService: SalesOrders,
    @Inject(CustomerSalesOrdersService) private customerSalesOrdersService: SalesOrders,
  ) {}

  buildId(businessId: string, orderId: string): string {
    return [businessId, orderId].join(':');
  }

  public getOrderState(orderId: string, businessId: string): Observable<OrderState> {
    const id = this.buildId(businessId, orderId);
    if (!this.ordersStore.has(id)) {
      this.fetchOrder(orderId, businessId);
    }
    return this.ordersStore.get(id).pipe(skipWhile((o) => o === undefined));
  }

  fetchOrder(orderId: string, businessId: string): void {
    const id = this.buildId(businessId, orderId);
    if (!this.ordersStore.has(id)) {
      this.ordersStore.set(id, new BehaviorSubject<OrderState>(undefined));
    }
    const order$$ = this.ordersStore.get(id);
    if (!businessId || !orderId) {
      order$$.next({ order: null, loading: false });
      return;
    }
    const currentValue = this.ordersStore.get(id).getValue();
    if (currentValue) {
      order$$.next({ order: currentValue.order, loading: true });
    }
    this.salesOrdersService
      .get(orderId, businessId)
      .pipe(
        catchError(() => {
          return this.customerSalesOrdersService.get(orderId, businessId);
        }),
        tap((o) => order$$.next({ order: o, loading: false })),
        take(1),
      )
      .subscribe();
  }

  public refreshOrder(orderId: string, businessId: string): void {
    this.fetchOrder(orderId, businessId);
  }

  public clearOrderStateCache(): void {
    this.ordersStore.clear();
  }
}
